/**
 * Reservations API client (BON-382).
 *
 * A reservation is seats a CSR holds for a customer who then pays through the
 * ordinary checkout, via a link emailed to them. It is the same underlying
 * record as a customer's own checkout, seen from the staff side.
 *
 * NOTE FOR ANYONE LOOKING FOR THE LINK: there isn't one in these responses, and
 * that is deliberate (ADR-0023). The credential goes to the customer's inbox and
 * nowhere else — a copyable link would end up pasted into Slack, and a CSR
 * opening it to check that it works would consume the customer's ten-minute
 * window, because nothing distinguishes their browser from the customer's.
 */
import { apiFetch } from '../lib/apiFetch';

/** Where a reservation is in its life. Derived server-side from the row's
 *  status, activation stamp, expiry and payment attempts — never stored, so it
 *  cannot go stale. */
export type ReservationStage =
  | 'link_sent'
  | 'engaged'
  | 'card_declined'
  // Terminal (BON-472). The customer was declined the maximum number of times,
  // so the record is `DECLINED` and can never take money — distinct from
  // `card_declined`, which is still payable. THE SEATS ARE ALREADY BACK: the
  // strike-out releases the hold in the same breath as writing the status, so
  // nothing here is waiting on a CSR to free capacity.
  | 'attempts_exhausted'
  | 'paying'
  | 'expired'
  // Somebody gave the seats back — a CSR, the customer's own Clear cart, or a
  // cancelled workshop. Terminal, and only ever reachable through the history
  // view: the default list is active reservations only (BON-472).
  | 'released'
  | 'needs_review';

export interface ReservationRow {
  id: string;
  /** Short display handle, e.g. `RSV-3F7A2B`. Derived from the id: a
   *  reservation has no confirmation code until it becomes a booking. */
  reference: string;
  customerName: string | null;
  customerEmail: string | null;
  workshopId: string;
  workshopName: string | null;
  venueName: string | null;
  /** ISO datetime — venue wall clock. */
  workshopStartsAt: string | null;
  seats: number;
  /**
   * Seats this reservation is holding RIGHT NOW, counted server-side from the
   * holds table (BON-472).
   *
   * NOT `seats`, which is the ticket count — what the customer is buying. This is
   * what would come back if a CSR released it, and it is what decides whether
   * Release is offered at all: a row holding nothing has nothing to hand back,
   * and the button there wrote an audit entry claiming seats that never moved.
   *
   * It cannot be inferred from `stage`. A struck-out reservation whose seat
   * release failed — the release is best-effort and swallows its own failure — is
   * terminal AND still holding, and Release is the only route those seats have
   * back short of a database edit.
   */
  seatsHeld: number;
  stage: ReservationStage;
  totalCents: number;
  currency: string;
  createdAt: string;
  expiresAt: string;
  reservedBy: { id: string; name: string } | null;
  /**
   * The booking link never reached the customer, so nothing in their inbox can
   * open this checkout.
   *
   * THE REMEDY IS RESEND (BON-472), which mints a fresh credential — not release
   * and re-create, which hands back the seats on a workshop that may be nearly
   * full to fix something a button now fixes. Recency-bounded server-side, so a
   * later successful send clears it.
   */
  linkSendFailed: boolean;
}

export interface ReservationListResult {
  reservations: ReservationRow[];
  /**
   * Seats these reservations are holding RIGHT NOW, read server-side from the
   * holds table rather than inferred from a stage or a deadline (BON-472).
   *
   * NORMALLY UNMOVED BY THE HISTORY TOGGLE, because the rows it reveals are
   * finished and have already given their seats back.
   *
   * NOT A GUARANTEE, and this is a known accepted gap (operator's call,
   * 2026-08-25). The default view queries active statuses only, so a struck-out
   * reservation whose automatic seat release FAILED — the release is best-effort
   * and swallows its own error — is neither listed nor counted here until someone
   * turns the history view on. Its seats are genuinely held and this figure will
   * understate them. Flipping the toggle is what surfaces both the row and its
   * seats, and Release there is how they come back.
   */
  seatsHeld: number;
  /** Drives the tab badge: how many reservations are actually holding seats.
   *  Same source as {@link seatsHeld}, so the two cannot disagree — and the
   *  history toggle leaves it alone for the same reason. */
  outstandingCount: number;
}

export interface CreateReservationInput {
  workshopId: string;
  ticketCount: number;
  customerName: string;
  customerEmail: string;
}

export interface CreateReservationResult {
  id: string;
  ticketCount: number;
  expiresAt: string;
  /** Where the link went. The link itself is not returned. ABSENT on the
   *  duplicate path, which echoes only the existing row — fall back to the
   *  address the CSR typed rather than rendering an empty string. */
  emailedTo?: string;
  /** False when the send failed. The reservation still holds its seats and that
   *  credential is gone, but a resend mints a new one (BON-472) — release and
   *  redo is no longer the remedy.
   *  Absent on the duplicate path, where the first submit already sent it. */
  emailed?: boolean;
  /** Present when the server collapsed a double-submit into the existing
   *  reservation instead of creating a second one. */
  duplicate?: boolean;
}

/**
 * The state refusals the rotation endpoints answer with (BON-472). Every one of
 * them is a 409 except `RESERVATION_NOT_FOUND`, and every one carries a
 * CSR-facing `message` that may be shown verbatim.
 *
 * The union exists so a caller can branch on WHICH refusal without matching on
 * prose, and so a refusal can be told apart from a request that never landed:
 * a code means the server decided something, which is the whole basis for
 * telling a CSR that nothing changed.
 */
export type ReservationRefusalCode =
  | 'RESERVATION_NOT_FOUND'
  | 'RESERVATION_LAPSED'
  | 'WORKSHOP_ENDED'
  | 'WORKSHOP_DISABLED'
  | 'PAYMENT_IN_FLIGHT'
  | 'ALREADY_PAID'
  | 'ATTEMPTS_EXHAUSTED'
  | 'NEEDS_REVIEW'
  | 'ALREADY_RELEASED'
  | 'ROTATION_CONFLICT';

/**
 * The status a request that never got an answer is reported with (BON-472).
 *
 * A dropped connection, a DNS failure or a token refresh that threw all reject
 * `fetch` rather than returning a `Response`, so there is no status to read —
 * and that is a MATERIALLY DIFFERENT FACT from a refusal. The server may well
 * have carried the request out. Callers key the "we genuinely do not know"
 * wording off this, so it must not be collapsed into 500 or into a bare Error.
 */
export const NO_RESPONSE_STATUS = 0;

/** Thrown so callers can render an inline message keyed on the status. */
export class ReservationError extends Error {
  constructor(
    message: string,
    /** The HTTP status, or {@link NO_RESPONSE_STATUS} when nothing answered. */
    readonly status: number,
    /** Present only on a STATE refusal. Absent on 400/401/403, which are about
     *  the request or the caller rather than the reservation. */
    readonly code?: ReservationRefusalCode,
  ) {
    super(message);
    this.name = 'ReservationError';
  }
}

/**
 * Whether the SERVER's own sentence is fit to show a CSR.
 *
 * Only for responses THIS API authors. A 401 is written by the JWT middleware and
 * a 5xx by the framework or an unhandled throw, and neither has any idea a CSR is
 * reading it: a genuinely expired token renders as
 * `"exp" claim timestamp check failed`, which is what shipped and what a CSR
 * actually saw on the reservations tab.
 *
 * So this is not defensive tidying — `body.message || fallback` meant the
 * fallbacks below were UNREACHABLE for exactly the two statuses whose messages
 * are unfit, because the middleware always sends one.
 */
function serverMessageIsForHumans(status: number): boolean {
  return status !== 401 && status < 500;
}

async function errorFrom(res: Response): Promise<ReservationError> {
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  const fallback =
    res.status === 401
      ? 'Your session has expired. Sign in again to continue.'
      : res.status === 403
        ? 'You do not have permission to do that in this region.'
        : res.status === 404
          ? 'That workshop is not open for booking.'
          : res.status === 409
            ? 'Those seats are no longer available.'
            : 'Something went wrong. Please try again.';
  const message = serverMessageIsForHumans(res.status) ? body.message || fallback : fallback;
  return new ReservationError(message, res.status);
}

/**
 * The Reservations tab's rows.
 *
 * `includeInactive` adds the finished ones — lapsed, released and struck out —
 * to the active ones a CSR can still act on. The value is sent as the literal
 * string the server's enum accepts: it validates `true`/`false` and answers 400
 * to anything else, deliberately, so that `?includeInactive=false` cannot be
 * read as "yes" the way a coerced boolean would read it.
 */
export async function listReservations(includeInactive = false): Promise<ReservationListResult> {
  const res = await apiFetch(`/api/v2/reservations?includeInactive=${includeInactive}`);
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as ReservationListResult;
}

export async function createReservation(
  input: CreateReservationInput,
): Promise<CreateReservationResult> {
  const res = await apiFetch('/api/v2/reservations', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await errorFrom(res);

  const body = (await res.json()) as CreateReservationResult & { status?: string };
  // A duplicate answers 200 rather than 201 and carries the EXISTING row. Not an
  // error — the first submit already emailed the customer.
  return { ...body, duplicate: body.status === 'duplicate' };
}

export interface ReleaseReservationResult {
  status: 'released' | 'already_released';
  seatsReturned?: number;
}

export async function releaseReservation(
  id: string,
  reason: string,
): Promise<ReleaseReservationResult> {
  const res = await apiFetch(`/api/v2/reservations/${id}/release`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    // 409 here means a payment is in flight, which is a refusal worth wording
    // precisely — the seats are deliberately NOT released.
    if (res.status === 409) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      throw new ReservationError(
        body.message || 'This reservation has a payment in progress and cannot be released yet.',
        409,
      );
    }
    throw await errorFrom(res);
  }
  return (await res.json()) as ReleaseReservationResult;
}

/**
 * What each endpoint says when the server refused without a sentence of its own.
 *
 * Per endpoint rather than one shared string, because the two actions fail into
 * different remedies: a resend is worth pressing again, and an address that
 * would not save is worth coming back to. Every state refusal carries its own
 * CSR-facing `message`, so these are only ever reached by an unlabelled failure.
 */
const RESEND_FAILED_GENERIC = 'We couldn’t send the payment link. Please try again.';
const CONTACT_UPDATE_FAILED_GENERIC =
  'We were unable to update the email address. Please try again later.';

/**
 * A refusal from one of the rotation endpoints (BON-472).
 *
 * Its own mapper rather than `errorFrom`, because that one's fallbacks are
 * creation's: a 404 there means "that workshop is not open for booking", which
 * on these endpoints would be a lie — a 404 here means the reservation is not
 * one this staff surface can act on. Every state refusal carries a `message`
 * written for a CSR, so the fallbacks are only ever reached by an infrastructure
 * failure.
 */
async function rotationErrorFrom(res: Response, generic: string): Promise<ReservationError> {
  const body = (await res.json().catch(() => ({}))) as {
    message?: string;
    code?: ReservationRefusalCode;
  };
  const fallback =
    res.status === 401
      ? 'Your session has expired. Sign in again and retry.'
      : res.status === 403
        ? 'You do not have permission to do that in this region.'
        : res.status === 404
          ? 'That reservation no longer exists.'
          : generic;
  // Same rule as the list path: a 401's sentence comes from the JWT middleware
  // and a 5xx's from the framework, so neither may reach a CSR. Without this the
  // 401 branch above is dead code, because the middleware always sends a message.
  const message = serverMessageIsForHumans(res.status) ? body.message || fallback : fallback;
  return new ReservationError(message, res.status, body.code);
}

/**
 * `apiFetch`, with a rejection turned into a `ReservationError` carrying
 * {@link NO_RESPONSE_STATUS} (BON-472).
 *
 * WITHOUT THIS THE STATUS DOES NOT SURVIVE TO THE CALLER. A dropped connection
 * rejects with a bare `TypeError('Failed to fetch')`, and a caller that can only
 * ask `instanceof ReservationError` reads that as "not one of ours" and shows
 * whatever generic refusal wording it has — which claims nothing happened, on
 * the one path where the request may well have gone through and killed the
 * customer's link.
 */
async function rotationFetch(url: string, options: RequestInit): Promise<Response> {
  try {
    return await apiFetch(url, options);
  } catch {
    // Deliberately not the thrown message: `Failed to fetch` is a browser
    // internal, and this string is read by a CSR on the phone to a customer.
    throw new ReservationError('We could not reach the server.', NO_RESPONSE_STATUS);
  }
}

export interface ResendReservationLinkResult {
  status: 'resent';
  /** The re-armed reservation window. */
  expiresAt: string;
  emailedTo: string;
  /**
   * FALSE IS NOT A FAILED REQUEST — the rotation committed and the customer's
   * old link is already dead, but the new one did not leave the building. The
   * only recovery is another resend, and the caller has to say so.
   */
  emailed: boolean;
}

/**
 * Mint a new credential and email the customer a fresh link.
 *
 * There is no request body: the reservation's own row supplies everything, and
 * the old link cannot be re-sent because only a digest of it is stored
 * (ADR-0023). Resending therefore always rotates.
 */
export async function resendReservationLink(id: string): Promise<ResendReservationLinkResult> {
  const res = await rotationFetch(`/api/v2/reservations/${id}/resend`, { method: 'POST' });
  if (!res.ok) throw await rotationErrorFrom(res, RESEND_FAILED_GENERIC);
  return (await res.json()) as ResendReservationLinkResult;
}

export interface UpdateReservationContactInput {
  customerName: string;
  customerEmail: string;
}

export interface UpdateReservationContactResult {
  status: 'contact_updated';
  /**
   * Whether the email GENUINELY changed, which is the server's determination and
   * never the browser's — it compares trimmed and lowercased on both sides.
   *
   * `true`: a new link went out and whatever preceded it is dead. `false`:
   * name-only, a case-only retype, or nothing changed — nothing was emailed and
   * nothing about the credential moved.
   *
   * FALSE IS NOT A PROMISE THAT THE CUSTOMER HAS A WORKING LINK. It says only
   * that this request did not change one. A reservation whose first email never
   * left the building answers `false` too, and telling a CSR their link still
   * works is what sent a customer looking in a spam folder for nothing.
   */
  credentialRotated: boolean;
  expiresAt: string;
  emailedTo: string;
  /** Meaningful only when `credentialRotated` is true. False alongside a false
   *  rotation means "nothing needed sending", NOT a failed send. */
  emailed: boolean;
}

/** Correct the name and email on a reservation. Both fields are required — this
 *  is a whole-contact write, not a patch of the field the CSR touched. */
export async function updateReservationContact(
  id: string,
  input: UpdateReservationContactInput,
): Promise<UpdateReservationContactResult> {
  const res = await rotationFetch(`/api/v2/reservations/${id}/contact`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await rotationErrorFrom(res, CONTACT_UPDATE_FAILED_GENERIC);
  return (await res.json()) as UpdateReservationContactResult;
}
